import React, { useEffect, lazy } from "react";
import { Routes, Route, Link as RouterLink } from "react-router-dom";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Paper,
  Link,
  Box,
  Container,
  Divider,
} from "@mui/material";
import { styled } from "@mui/system";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PublicIcon from '@mui/icons-material/Public';

const DataDownload = lazy(() => import("./support/DataDownload"));
const TrainingVideos = lazy(() => import("./TraningVideos/TrainingVideos"));

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[6],
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));

const CardIcon = styled("div")(({ theme }) => ({
  fontSize: "40px",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

// Assign icons to items dynamically (optional)
const getIcon = (title) => {
  switch (title) {
    case "Account Support":
    case "Support Ticket":
      return <SupportAgentIcon />;
    case "Data Download":
      return <FileDownloadIcon />;
    case "Training Videos":
      return <VideoLibraryIcon />;
    case "Dubai Speed Radar Limit":
    case "UAE Petrol Prices":
      return <PublicIcon />;
    case "FAQ":
    default:
      return <HelpOutlineIcon />;
  }
};

const supportLinks = [
  {
    title: "Account Support",
    type: "external",
    link: "mailto:support@itcshj.ae",
  },
  {
    title: "Support Ticket",
    type: "external",
    link: "http://infofleetsupport.com/",
  },
  {
    title: "Dubai Speed Radar Limit",
    type: "external",
    link: "https://www.dubaipolice.gov.ae/wps/portal/home/opendata/Street_Speed_Limits",
  },
  {
    title: "UAE Petrol Prices",
    type: "external",
    link: "https://www.bayut.com/mybayut/petrol-prices-uae-update/",
  },
  {
    title: "Training Videos",
    type: "internal",
    link: "/Support/TrainingVideos",
  },
  {
    title: "Data Download",
    type: "internal",
    link: "/Support/DataDownload",
  },
  {
    title: "FAQ",
    type: "external",
    link: "https://infofleetcom.tawk.help/",
  },
];

export default function Support() {
  return (
    <>
      <Routes>
        <Route path="/TrainingVideos" element={<TrainingVideos />} />
        <Route path="/DataDownload" element={<DataDownload />} />
        <Route
          path="/"
          element={
            <Box sx={{ backgroundColor: "#f9f9f9", py: 4 }}>
              <Container maxWidth="lg">
                <Typography variant="h4" gutterBottom align="center">
                  Support Center
                </Typography>
                <Typography
                  variant="subtitle1"
                  align="center"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  Access comprehensive training materials, dedicated support channels, and a wide range of helpful resources to guide you every step of the way.
                </Typography>

                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",         // 1 card per row on mobile
                    sm: "1fr 1fr",     // 2 cards per row on small screens
                    md: "1fr 1fr 1fr 1fr",  // 4 cards per row on medium+
                  }}
                  gap={3}
                >
                  {supportLinks.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      {item.type === "internal" ? (
                        <Link
                          component={RouterLink}
                          to={item.link}
                          underline="none"
                          color="inherit"
                        >
                          <StyledCard>
                            <CardContent sx={{ textAlign: "center" }}>
                              <CardIcon>{getIcon(item.title)}</CardIcon>
                              <Typography variant="h6">{item.title}</Typography>
                            </CardContent>
                          </StyledCard>
                        </Link>
                      ) : (
                        <Link
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="none"
                          color="inherit"
                        >
                          <StyledCard>
                            <CardContent sx={{ textAlign: "center" }}>
                              <CardIcon>{getIcon(item.title)}</CardIcon>
                              <Typography variant="h6">{item.title}</Typography>
                            </CardContent>
                          </StyledCard>
                        </Link>
                      )}
                    </Grid>
                  ))}
                </Box>
              </Container>
            </Box>
          }
        />
      </Routes>
    </>
  );
}
